import { Inject, Injectable, NgZone } from '@angular/core';
import { LOCAL_STORAGE, WINDOW } from '@core/injectors';
import { ConfigService } from '@core/services';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  interval,
  merge,
  Observable,
  Subscription,
  tap,
} from 'rxjs';

const DEFAULT_IDLE_TIME = 28 * 60 * 1000; // 28 min
const DEFAULT_TIMEOUT_TIME = 2 * 60 * 1000; // 2 min

@Injectable()
export class IdleService {
  public readonly STORAGE_KEY: string = 'act';
  private readonly idleTime: number;
  private readonly timeoutTime: number;
  private readonly generalTimeoutTime: number;

  private state: 'ACTIVE' | 'COUNTDOWN' = 'ACTIVE';
  private timerSubscription?: Subscription;

  private readonly idleCountdown$ = new BehaviorSubject<number | null>(null);
  private readonly timeout$ = new BehaviorSubject<boolean>(false);

  constructor(
    private ngZone: NgZone,
    private readonly configService: ConfigService,
    @Inject(WINDOW) private win: Window,
    @Inject(LOCAL_STORAGE) private storage: Storage,
  ) {
    this.idleTime = this.configService.idleTime || DEFAULT_IDLE_TIME;
    this.timeoutTime = this.configService.timeoutTime * 1000 || DEFAULT_TIMEOUT_TIME;
    this.generalTimeoutTime = this.idleTime + this.timeoutTime;
  }

  public startWatching() {
    this.storage.setItem(this.STORAGE_KEY, new Date().toISOString());
    this.state = 'ACTIVE';

    // Listen for activity, but only update the timestamp if the user is in the ACTIVE state.
    merge(fromEvent(this.win, 'mousemove'), fromEvent(this.win, 'keydown'), fromEvent(this.win, 'click'))
      .pipe(
        debounceTime(100),
        tap(() => {
          if (this.state === 'ACTIVE') {
            this.storage.setItem(this.STORAGE_KEY, new Date().toISOString());
          }
        }),
      )
      .subscribe();

    // The main timer that checks the state every second.
    this.timerSubscription = interval(1000).subscribe(() => this.checkIdleState());

    // Instantly check state when the tab regains focus.
    fromEvent(this.win, 'focus')
      .pipe(tap(() => this.checkIdleState()))
      .subscribe();
  }

  public stopWatching() {
    this.timerSubscription?.unsubscribe();
    this.storage.removeItem(this.STORAGE_KEY);
  }

  // Called by the component's "Continue" button.
  public stopTimer() {
    this.storage.setItem(this.STORAGE_KEY, new Date().toISOString());
    this.state = 'ACTIVE';
    this.idleCountdown$.next(null);
  }

  // Called by the component's storage listener.
  public resetTimer() {
    this.checkIdleState();
  }

  public onTimerStart(): Observable<number | null> {
    return this.idleCountdown$.asObservable().pipe(distinctUntilChanged());
  }

  public onTimeout(): Observable<boolean> {
    return this.timeout$.asObservable().pipe(filter((v) => v));
  }

  private checkIdleState() {
    const lastActivity = this.storage.getItem(this.STORAGE_KEY);
    if (!lastActivity) return;

    // Calculate how many milliseconds have passed since the last activity.
    const elapsed = Date.now() - new Date(lastActivity).getTime();

    this.ngZone.run(() => {
      if (elapsed >= this.generalTimeoutTime) {
        if (!this.timeout$.value) this.timeout$.next(true);

        // If idle time is reached, the countdown started and ignore further activity.
      } else if (elapsed >= this.idleTime) {
        this.state = 'COUNTDOWN';
        const remaining = Math.ceil((new Date(lastActivity).getTime() + this.generalTimeoutTime - Date.now()) / 1000);
        if (this.idleCountdown$.value !== remaining) this.idleCountdown$.next(remaining);

        // Otherwise, the user is still active.
      } else {
        if (this.state === 'COUNTDOWN') this.idleCountdown$.next(null);
        this.state = 'ACTIVE';
      }
    });
  }
}
